import React, { useState } from 'react';
import { useTable } from '../../../waiter/context/TableContext';
import { addDoc, collection, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import TableLayout from './TableLayout';
import FloorPlan from './FloorPlan';
import Reservations from './Reservations';
import './AdminTableManagement.css';

const AdminTableManagement = () => {
  const { tables } = useTable();
  const [activeTab, setActiveTab] = useState('layout');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTable, setNewTable] = useState({
    tableNumber: '',
    capacity: 4,
    floor: 'Ground Floor',
    section: 'Main Area',
    shape: 'square',
    positionX: 100,
    positionY: 100
  });

  const handleAddTable = async () => {
    try {
      await addDoc(collection(db, 'tables'), {
        ...newTable,
        status: 'available',
        createdAt: new Date(),
        currentOrder: null,
        reservations: []
      });
      setShowAddForm(false);
      setNewTable({ 
        tableNumber: '', 
        capacity: 4, 
        floor: 'Ground Floor', 
        section: 'Main Area',
        shape: 'square',
        positionX: 100,
        positionY: 100
      });
      alert('Table added successfully!');
    } catch (error) {
      console.error('Error adding table:', error);
      alert('Failed to add table');
    }
  };

  const handleDeleteTable = async (tableId) => {
    if (window.confirm('Are you sure you want to delete this table?')) {
      try {
        await deleteDoc(doc(db, 'tables', tableId));
        alert('Table deleted successfully!');
      } catch (error) {
        console.error('Error deleting table:', error);
        alert('Failed to delete table');
      }
    }
  };

  const handleUpdateTablePosition = async (tableId, positionX, positionY) => {
    try {
      await updateDoc(doc(db, 'tables', tableId), {
        positionX,
        positionY
      });
    } catch (error) {
      console.error('Error updating table position:', error);
    }
  };

  const handleUpdateTableStatus = async (tableId, status) => {
    try {
      await updateDoc(doc(db, 'tables', tableId), {
        status
      });
    } catch (error) {
      console.error('Error updating table status:', error);
      alert('Failed to update table status');
    }
  };

  const getTableStats = () => {
    const total = tables.length;
    const available = tables.filter(t => t.status === 'available').length;
    const occupied = tables.filter(t => t.status === 'occupied').length;
    const reserved = tables.filter(t => t.status === 'reserved').length;
    const cleaning = tables.filter(t => t.status === 'cleaning').length;
    const totalCapacity = tables.reduce((sum, t) => sum + (t.capacity || 0), 0);
    
    return { total, available, occupied, reserved, cleaning, totalCapacity };
  };

  const stats = getTableStats();

  return (
    <div className="admin-table-management">
      <div className="page-header">
        <div>
          <h1>Table Management</h1>
          <p>Manage restaurant tables, layout, and reservations</p>
        </div>
        <button className="btn-add-table" onClick={() => setShowAddForm(true)}>
          + Add New Table
        </button>
      </div>

      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-icon" style={{background: '#3498db'}}>🪑</div>
          <div className="stat-info">
            <p className="stat-label">Total Tables</p>
            <p className="stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{background: '#2ecc71'}}>✅</div>
          <div className="stat-info">
            <p className="stat-label">Available</p>
            <p className="stat-value">{stats.available}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{background: '#e74c3c'}}>🔴</div>
          <div className="stat-info">
            <p className="stat-label">Occupied</p>
            <p className="stat-value">{stats.occupied}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{background: '#f39c12'}}>📅</div>
          <div className="stat-info">
            <p className="stat-label">Reserved</p>
            <p className="stat-value">{stats.reserved}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{background: '#9b59b6'}}>🧹</div>
          <div className="stat-info">
            <p className="stat-label">Cleaning</p>
            <p className="stat-value">{stats.cleaning}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{background: '#34495e'}}>👥</div>
          <div className="stat-info">
            <p className="stat-label">Total Capacity</p>
            <p className="stat-value">{stats.totalCapacity}</p>
          </div>
        </div>
      </div>

      {showAddForm && (
        <div className="modal-overlay" onClick={() => setShowAddForm(false)}>
          <div className="add-table-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Table</h3>
              <button onClick={() => setShowAddForm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Table Number *</label>
                <input 
                  type="text"
                  placeholder="e.g., T1, A1, 101"
                  value={newTable.tableNumber}
                  onChange={(e) => setNewTable({...newTable, tableNumber: e.target.value})}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Capacity *</label>
                  <input 
                    type="number"
                    min="1"
                    max="20"
                    value={newTable.capacity}
                    onChange={(e) => setNewTable({...newTable, capacity: parseInt(e.target.value)})}
                  />
                </div>
                <div className="form-group">
                  <label>Shape</label>
                  <select 
                    value={newTable.shape}
                    onChange={(e) => setNewTable({...newTable, shape: e.target.value})}
                  >
                    <option value="square">Square</option>
                    <option value="rectangle">Rectangle</option>
                    <option value="circle">Circle</option>
                    <option value="oval">Oval</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Floor</label>
                  <select 
                    value={newTable.floor}
                    onChange={(e) => setNewTable({...newTable, floor: e.target.value})}
                  >
                    <option>Ground Floor</option>
                    <option>First Floor</option>
                    <option>Second Floor</option>
                    <option>Rooftop</option>
                    <option>Outdoor</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Section</label>
                  <input 
                    type="text"
                    placeholder="e.g., Main Area, VIP, Window"
                    value={newTable.section}
                    onChange={(e) => setNewTable({...newTable, section: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button 
                className="btn-save" 
                onClick={handleAddTable}
                disabled={!newTable.tableNumber}
              >
                Add Table
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="tab-navigation">
        <button 
          className={`tab-btn ${activeTab === 'layout' ? 'active' : ''}`}
          onClick={() => setActiveTab('layout')}
        >
          📐 Visual Layout
        </button>
        <button 
          className={`tab-btn ${activeTab === 'floor' ? 'active' : ''}`}
          onClick={() => setActiveTab('floor')}
        >
          🏢 Floor Plans
        </button>
        <button 
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          📋 Table List
        </button>
        <button 
          className={`tab-btn ${activeTab === 'reservations' ? 'active' : ''}`}
          onClick={() => setActiveTab('reservations')}
        >
          📅 Reservations
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'layout' && (
          <TableLayout 
            tables={tables}
            onUpdatePosition={handleUpdateTablePosition}
            onUpdateStatus={handleUpdateTableStatus}
            onDelete={handleDeleteTable}
          />
        )}
        
        {activeTab === 'floor' && (
          <FloorPlan 
            tables={tables}
            onUpdatePosition={handleUpdateTablePosition}
            onUpdateStatus={handleUpdateTableStatus}
          />
        )}
        
        {activeTab === 'list' && (
          <div className="table-list-view">
            <div className="list-header">
              <input 
                type="text" 
                placeholder="Search tables..." 
                className="search-input"
              />
              <select className="filter-select">
                <option value="all">All Floors</option>
                <option value="ground">Ground Floor</option>
                <option value="first">First Floor</option>
                <option value="outdoor">Outdoor</option>
              </select>
            </div>
            <table className="tables-table">
              <thead>
                <tr>
                  <th>Table #</th>
                  <th>Capacity</th>
                  <th>Floor</th>
                  <th>Section</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tables.map(table => (
                  <tr key={table.id}>
                    <td className="table-number">{table.tableNumber}</td>
                    <td>{table.capacity} people</td>
                    <td>{table.floor}</td>
                    <td>{table.section}</td>
                    <td>
                      <select 
                        value={table.status}
                        onChange={(e) => handleUpdateTableStatus(table.id, e.target.value)}
                        className={`status-select status-${table.status}`}
                      >
                        <option value="available">Available</option>
                        <option value="occupied">Occupied</option>
                        <option value="reserved">Reserved</option>
                        <option value="cleaning">Cleaning</option>
                      </select>
                    </td>
                    <td>
                      <button 
                        className="btn-delete-small"
                        onClick={() => handleDeleteTable(table.id)}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {activeTab === 'reservations' && (
          <Reservations tables={tables} />
        )}
      </div>
    </div>
  );
};

export default AdminTableManagement;
